import { AccountNavigationBlockMapper } from 'ish-core/models/account-navigation-block/account-navigation-block.mapper';
import { AccountNavigationBlock } from 'ish-core/models/account-navigation-block/account-navigation-block.model';

import { UserData } from './user.interface';
import { User } from './user.model';

/**
 * Name of the ICM custom field of the user profile that holds the ids of the account navigation items
 * the user marked as favorite. Change it here if the ICM names the custom field differently.
 */
export const ACCOUNT_NAVIGATION_FAVORITES_FIELD = 'accountNavigationFavorites';

/**
 * Name of the ICM custom field of the user profile that holds the navigation cards the user created,
 * one JSON string per card. Change it here if the ICM names the custom field differently.
 */
export const ACCOUNT_NAVIGATION_BLOCKS_FIELD = 'accountNavigationBlocks';

export class UserMapper {
  static fromData(user: UserData): User {
    // is used for business users

    return user
      ? {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          login: user.login,
          preferredLanguage: user.preferredLanguage,
          title: user.title,
          phoneHome: user.phoneHome,
          phoneBusiness: user.phoneBusiness,
          phoneMobile: user.phoneMobile,
          fax: user.fax,
          preferredInvoiceToAddressUrn: user.preferredInvoiceToAddress?.urn,
          preferredShipToAddressUrn: user.preferredShipToAddress?.urn,
          preferredPaymentInstrumentId: user.preferredPaymentInstrument?.id,
          birthday: user.birthday,
          availableSoldTos: user.availableSoldTos, // ICSA Customization
          businessPartnerNo: user.businessPartnerNo,
          department: user.department,
          customFields: user.customFields,
          favoriteIds: UserMapper.favoriteIdsFromCustomFields(user.customFields),
          navigationBlocks: UserMapper.navigationBlocksFromCustomFields(user.customFields),
        }
      : undefined;
  }

  /**
   * Reads the account navigation favorites from the custom fields of the user profile. A user who never
   * marked a favorite has no such custom field yet, which is mapped to an empty list.
   */
  static favoriteIdsFromCustomFields(customFields: Record<string, unknown>): string[] {
    const value = customFields?.[ACCOUNT_NAVIGATION_FAVORITES_FIELD];

    if (!value) {
      return [];
    }
    // a multi value custom field arrives as an array, a single remaining value may arrive as a string
    return (Array.isArray(value) ? value : [value]).map(String);
  }

  /**
   * Writes the account navigation favorites into the custom fields of the user profile. The other
   * custom fields are passed through so that saving the favorites cannot discard them.
   */
  static customFieldsWithFavoriteIds(
    customFields: Record<string, unknown>,
    favoriteIds: string[]
  ): Record<string, unknown> {
    return { ...customFields, [ACCOUNT_NAVIGATION_FAVORITES_FIELD]: favoriteIds ?? [] };
  }

  /**
   * Reads the navigation cards the user created from the custom fields of the user profile. Entries
   * that cannot be read are dropped, so a single broken value never takes the whole navigation down.
   */
  static navigationBlocksFromCustomFields(customFields: Record<string, unknown>): AccountNavigationBlock[] {
    const value = customFields?.[ACCOUNT_NAVIGATION_BLOCKS_FIELD];

    if (!value) {
      return [];
    }
    // a multi value custom field arrives as an array, a single remaining value may arrive as a string
    return (Array.isArray(value) ? value : [value])
      .map(entry => AccountNavigationBlockMapper.fromJson(entry))
      .filter(block => !!block);
  }

  /**
   * Writes the navigation cards of the user into the custom fields of the user profile. The other
   * custom fields are passed through so that saving a card cannot discard them.
   */
  static customFieldsWithNavigationBlocks(
    customFields: Record<string, unknown>,
    navigationBlocks: AccountNavigationBlock[]
  ): Record<string, unknown> {
    return {
      ...customFields,
      [ACCOUNT_NAVIGATION_BLOCKS_FIELD]: (navigationBlocks ?? []).map(AccountNavigationBlockMapper.toJson),
    };
  }

  /**
   * The custom fields to send with a user update, or undefined if the user carries none of the fields
   * this application maps out of them. Both the favorites and the navigation cards are folded into the
   * same object, so an update never writes one of them and drops the other.
   */
  static customFieldsForUpdate(
    user: Pick<User, 'customFields' | 'favoriteIds' | 'navigationBlocks'>
  ): Record<string, unknown> {
    if (!user?.favoriteIds && !user?.navigationBlocks) {
      return;
    }

    let customFields = user.customFields;
    if (user.favoriteIds) {
      customFields = UserMapper.customFieldsWithFavoriteIds(customFields, user.favoriteIds);
    }
    if (user.navigationBlocks) {
      customFields = UserMapper.customFieldsWithNavigationBlocks(customFields, user.navigationBlocks);
    }
    return customFields;
  }
}
